import type { PaginatedMeta, PaginationParams } from './client';
import { request, toQuery } from './client';

export type AppointmentStatus =
	| 'scheduled'
	| 'completed'
	| 'cancelled'
	| 'no_show';

export type AppointmentClient = {
	id: number;
	first_name: string;
	last_name: string;
	email: string;
	phone: string | null;
	timezone: string | null;
	status: string;
	notes: string | null;
	created_at: string;
	updated_at: string | null;
};

export type AppointmentCoach = {
	id: number;
	user_id: number;
	name: string;
	email: string;
	bio: string | null;
	created_at: string;
	updated_at: string | null;
};

export type AppointmentProgram = {
	id: number;
	title: string;
	description: string | null;
	is_active: boolean;
	coach_id: number;
	created_at: string;
	updated_at: string | null;
};

export type Appointment = {
	id: number;
	enrollment_id: number;
	scheduled_date: string;
	start_time: string;
	end_time: string;
	session_number: number;
	status: AppointmentStatus;
	notes: string | null;
	meeting_url: string | null;
	created_at: string;
	updated_at: string | null;
	client: AppointmentClient | null;
	coach: AppointmentCoach | null;
	program: AppointmentProgram | null;
};

export type AppointmentsListResponse = PaginatedMeta & {
	session_bookings: Appointment[];
};

export type AppointmentsListParams = PaginationParams & {
	search?: string;
	status?: AppointmentStatus;
	from_date?: string;
	to_date?: string;
	client_id?: number;
	coach_id?: number;
};

export type CreateAppointmentData = {
	enrollment_id: number;
	scheduled_date: string;
	start_time: string;
	end_time: string;
	notes?: string;
};

export type AppointmentBulkAction = 'complete' | 'cancel';

export type AppointmentBulkResult = {
	action: AppointmentBulkAction;
	processed: number;
	failed: number;
	results: Array<{ id: number; success: boolean; error?: string }>;
};

export const appointmentsApi = {
	list: (params?: AppointmentsListParams) =>
		request<AppointmentsListResponse>('/session-bookings' + toQuery(params)),

	get: (id: number) => request<Appointment>(`/session-bookings/${id}`),

	create: (data: CreateAppointmentData) =>
		request<Appointment>('/session-bookings', { method: 'POST', data }),

	complete: (id: number) =>
		request<Appointment>(`/session-bookings/${id}/complete`, {
			method: 'POST',
			data: {},
		}),

	cancel: (id: number) =>
		request<Appointment>(`/session-bookings/${id}/cancel`, {
			method: 'POST',
			data: {},
		}),

	bulk: (action: AppointmentBulkAction, ids: number[]) =>
		request<AppointmentBulkResult>('/session-bookings/bulk', {
			method: 'POST',
			data: { action, ids },
		}),
};
